/* eslint-disable react/no-danger */
import type { ApolloError } from '@apollo/client';
import { ToastButton, ToastTitle, ToastTopBar } from '@presentation/components';
import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import type { ToastOptions } from 'react-toastify';
import { useLog } from '@application/api';
import { toast } from 'react-toastify';
import { useRouter } from './useRouter';

const MAPPED_KEYS = {
  '\nInvalid `prisma.account.create()` invocation:\n\n\n  Unique constraint failed on the fields: (`email`)':
    'common.toasts.firebaseError.auth/email-already-in-use',
  'Unique constraint failed on the fields: (`email`':
    'common.toasts.apolloError.createAccount/account-already-exists',
  'TOO_MANY_ATTEMPTS_TRY_LATER : Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.':
    'common.toasts.apolloError.login/too-many-failed-attempts',
  'Access denied! You need to be authorized to perform this action!':
    'common.toasts.apolloError.Unauthorized',
  'The email address is already in use by another account.':
    'common.toasts.firebaseError.auth/email-already-in-use',
  'Context creation failed: auth/token-invalid': 'common.toasts.apolloError.auth/token-invalid',
  'There is no user record corresponding to the provided email.':
    'common.toasts.firebaseError.auth/user-not-found',
};

export const useShowToast = () => {
  const { translate } = useRouter();
  const { log } = useLog();

  const showToast = useCallback(
    (text: string, options: ToastOptions, linkState?: any) => {
      return showToastImplementation(translate, text, options, linkState);
    },
    [translate],
  );

  const showToastOnApolloError = useCallback(
    async (error: ApolloError, fallbackMessage?: string) => {
      await log({ content: 'Error', params: { error } });
      return showToastOnApolloErrorImplementation(translate, error, fallbackMessage);
    },
    [log, translate],
  );

  return { showToast, showToastOnApolloError };
};

export const showToastImplementation = (
  translate: TFunction,
  text: string,
  options: ToastOptions,
  linkState?: any,
) => {
  toast(
    (props) => (
      <>
        <ToastTopBar type={props.toastProps.type} />
        <ToastTitle>{translate(`common.toasts.title.${props.toastProps.type}`)}</ToastTitle>
        {text && <p dangerouslySetInnerHTML={{ __html: text }} />}
        {options.type === 'error' && (
          <ToastButton
            size="small"
            color="blue"
            link={{
              pathname: '/'
                .concat(window.location.href.split('/').slice(3, 5).join('/'))
                .concat('/contact'),
              ...(linkState && { state: { ...linkState } }),
            }}
          >
            {translate('common.buttons.reportError')}
          </ToastButton>
        )}
      </>
    ),
    { ...options, delay: 500 },
  );
};

export const showToastOnApolloErrorImplementation = (
  translate: TFunction,
  error: ApolloError,
  fallbackMessage?: string,
) => {
  const fallback = fallbackMessage ?? translate('common.toasts.apolloError.general');

  const firebaseCode =
    (error as any)?.errorInfo?.code ??
    error?.graphQLErrors?.[0]?.extensions?.exception?.errorInfo?.code ??
    error?.graphQLErrors?.[0]?.extensions?.exception?.code;

  const firebaseTranslation = firebaseCode
    ? translate(`common.toasts.firebaseError.${firebaseCode}`, {
        defaultValue: '',
      })
    : '';

  let translation = translate(`common.toasts.apolloError.${error?.message}`, {
    defaultValue: '',
  });

  // HACK
  const mapped = (MAPPED_KEYS as any)[error?.message];
  if (mapped) {
    translation = translate(mapped, {
      defaultValue: '',
    });
  }

  console.error({ ...error });

  const text = translation || firebaseTranslation || fallback;

  if (text) {
    return showToastImplementation(
      translate,
      text,
      { type: 'error' },
      {
        contactError: JSON.stringify(error),
      },
    );
  }
};

export const showToastOnLoginUseOtherPlatform = (translate: TFunction, error: ApolloError) => {
  const translation = translate(`common.toasts.apolloError.${error?.message}`, {
    defaultValue: '',
  });

  const useFacebook = error.message === 'login/use-facebook';
  const useGoogle = error.message === 'login/use-google';

  if (translation && (useFacebook || useGoogle)) {
    toast(
      () => (
        <>
          <ToastTopBar type="info" />
          <ToastTitle>{translate('common.toasts.title.info')}</ToastTitle>
          <p dangerouslySetInnerHTML={{ __html: translation }} />
        </>
      ),
      { delay: 500 },
    );
  }
};
